import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchBlogs, imgUrl } from "../api";
import { useSettings } from "../context/SettingsContext";

const ITEMS_PER_PAGE = 8;

function Pagination({ current, total, onChange }) {
  if (total <= 1) return null;
  const getPages = () => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [],
      start = Math.max(1, current - 2),
      end = Math.min(total, current + 2);
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("…");
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total) {
      if (end < total - 1) pages.push("…");
      pages.push(total);
    }
    return pages;
  };
  return (
    <div className="flex items-center justify-center gap-1 mt-12 mb-4">
      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-gold disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      {getPages().map((p, i) =>
        p === "…" ? (
          <span
            key={`el-${i}`}
            className="w-8 h-8 flex items-center justify-center text-stone-400 text-sm"
          >
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`w-8 h-8 rounded-full font-body text-sm transition-all duration-200 ${p === current ? "bg-gold text-white shadow font-medium" : "text-stone-500 hover:text-stone-900"}`}
          >
            {p}
          </button>
        ),
      )}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current === total}
        className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-gold disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
}

function BlogCard({ blog }) {
  const navigate = useNavigate();
  const imgSrc = blog.heroImg ? imgUrl(blog.heroImg) : "/images/about_us.jpg";
  const displayDate = blog.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <div
      onClick={() => navigate(`/blogs/${blog.slug || blog._id}`)}
      className="relative overflow-hidden rounded-br-[40px] group cursor-pointer"
      style={{ minHeight: "240px" }}
    >
      <img
        src={imgSrc}
        alt={blog.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
      {blog.tag && (
        <div className="absolute top-4 left-4">
          <span className="font-body text-white/80 text-[9px] tracking-widest uppercase bg-black/30 backdrop-blur-sm px-2.5 py-1 rounded-full">
            {blog.tag}
          </span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        {displayDate && (
          <p className="font-body text-white/50 text-[10px] tracking-widest uppercase mb-2">
            {displayDate}
          </p>
        )}
        <p className="font-display text-white text-lg md:text-xl font-light mb-2 leading-snug">
          {blog.title}
        </p>
        {blog.excerpt && (
          <p className="font-body text-white/60 text-xs md:text-sm leading-relaxed mb-4 line-clamp-2">
            {blog.excerpt}
          </p>
        )}
        <span className="font-body text-white text-[11px] tracking-[0.15em] uppercase flex items-center gap-2 group/btn">
          <span className="group-hover:text-gold transition-colors duration-200">
            Read More
          </span>
          <span className="w-4 h-[1px] bg-white group-hover:bg-gold group-hover:w-6 transition-all duration-300" />
        </span>
      </div>
    </div>
  );
}

function BlogCardSkeleton() {
  return (
    <div
      className="rounded-br-[40px] bg-stone-100 animate-pulse"
      style={{ minHeight: "240px" }}
    />
  );
}

export default function AllBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const { getBanner } = useSettings();

  useEffect(() => {
    setLoading(true);
    fetchBlogs({ page, limit: ITEMS_PER_PAGE })
      .then((d) => {
        setBlogs(d.data || []);
        setTotal(d.pagination?.total || 0);
        setTotalPages(d.pagination?.pages || 1);
      })
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
  }, [page]);

  const handlePageChange = (p) => {
    setPage(p);
    window.scrollTo({ top: 0 });
  };

  const bannerSrc = getBanner("blogs");

  return (
    <div className="min-h-screen bg-white pt-[60px]">
      {/* Banner */}
      <div className="relative h-44 sm:h-56 md:h-72 overflow-hidden">
        <img
          src={bannerSrc || "/images/bg3.png"}
          alt="Blogs & News"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex items-center justify-center px-4">
          <h1
            className="font-display text-white font-light text-center"
            style={{ fontSize: "clamp(1.8rem, 5.5vw, 3.8rem)" }}
          >
            Blogs &amp; News
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-10 lg:px-16 py-10 md:py-14">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <p className="font-body text-stone-500 text-sm">
            <span className="text-stone-900 font-medium">{total}</span> blog
            {total !== 1 ? "s" : ""}
          </p>
          {total > 0 && (
            <p className="font-body text-stone-400 text-xs">
              Page {page} / {totalPages}
            </p>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6 md:gap-x-6 md:gap-y-8">
            {[...Array(ITEMS_PER_PAGE)].map((_, i) => (
              <BlogCardSkeleton key={i} />
            ))}
          </div>
        ) : blogs.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6 md:gap-x-6 md:gap-y-8">
            {blogs.map((blog) => (
              <BlogCard key={blog._id} blog={blog} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="font-display text-stone-400 text-2xl font-light">
              No blogs found
            </p>
          </div>
        )}

        <Pagination
          current={page}
          total={totalPages}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
}
